/**
 * Smart Unit Converter — Sistem kepintaran konversi satuan
 * Handles: kg ↔ g, liter ↔ ml, kg ↔ ml (approximate for liquids ~1g/ml)
 */

// Kelompok satuan berat
const WEIGHT_UNITS = ['kg', 'gram', 'g', 'mg'];
// Kelompok satuan volume
const VOLUME_UNITS = ['liter', 'l', 'ml', 'cc'];

// Konversi semua ke base unit (gram untuk berat, ml untuk volume)
const TO_BASE = {
  kg: 1000,    // 1 kg = 1000 gram
  gram: 1,
  g: 1,
  mg: 0.001,
  liter: 1000, // 1 liter = 1000 ml
  l: 1000,
  ml: 1,
  cc: 1,
};

/**
 * Normalize satuan ke lowercase trim
 */
export function normalizeUnit(unit) {
  if (!unit) return '';
  return unit.toLowerCase().trim();
}

/**
 * Cek apakah dua satuan dalam kelompok yang sama (bisa dikonversi)
 */
export function isSameFamily(unitA, unitB) {
  const a = normalizeUnit(unitA);
  const b = normalizeUnit(unitB);
  const inWeight = u => WEIGHT_UNITS.includes(u);
  const inVolume = u => VOLUME_UNITS.includes(u);
  return (inWeight(a) && inWeight(b)) || (inVolume(a) && inVolume(b));
}

/**
 * Konversi nilai dari satu satuan ke satuan lain
 * Return: { value: number, unit: string, converted: boolean }
 */
export function convertUnit(value, fromUnit, toUnit) {
  const from = normalizeUnit(fromUnit);
  const to = normalizeUnit(toUnit);

  if (!from || !to || from === to) {
    return { value, unit: toUnit, converted: false };
  }

  if (!isSameFamily(from, to)) {
    return { value, unit: fromUnit, converted: false };
  }

  const baseFactorFrom = TO_BASE[from];
  const baseFactorTo = TO_BASE[to];

  if (!baseFactorFrom || !baseFactorTo) {
    return { value, unit: fromUnit, converted: false };
  }

  const converted = (value * baseFactorFrom) / baseFactorTo;
  return { value: converted, unit: toUnit, converted: true };
}

/**
 * Format angka agar tidak menampilkan decimal yang tidak perlu
 * Menggunakan locale id-ID: titik sebagai pemisah ribuan, koma sebagai desimal
 */
export function formatQty(value, maxDec = 2) {
  if (value == null) return '0';
  const num = Number(value);
  if (isNaN(num)) return '0';
  if (Number.isInteger(num)) {
    return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(num);
  }
  // Maksimum maxDec angka di belakang koma agar rapi dan tidak ada desimal tak perlu
  const rounded = parseFloat(num.toFixed(maxDec));
  return new Intl.NumberFormat('id-ID', { maximumFractionDigits: maxDec }).format(rounded);
}

/**
 * Smart display: tampilkan stok dalam satuan paling mudah dibaca
 * Contoh: 1500g → "1.5 kg", 0.5kg → "500 g", 1500ml → "1.5 liter"
 */
export function smartDisplayQty(value, unit) {
  if (value == null) return { display: '—', unit: unit || '' };
  const u = normalizeUnit(unit);
  const num = Number(value);

  // Berat: otomatis konversi g ↔ kg (ambang batas 100)
  if (WEIGHT_UNITS.includes(u)) {
    const grams = num * (TO_BASE[u] || 1);
    if (grams >= 100) {
      return { display: formatQty(grams / 1000, 2), unit: 'kg' };
    } else {
      return { display: formatQty(grams, 2), unit: 'g' };
    }
  }

  // Volume: otomatis konversi ml ↔ L (ambang batas 100)
  if (VOLUME_UNITS.includes(u)) {
    const ml = num * (TO_BASE[u] || 1);
    if (ml >= 100) {
      return { display: formatQty(ml / 1000, 2), unit: 'L' };
    } else {
      return { display: formatQty(ml, 2), unit: 'ml' };
    }
  }

  return { display: formatQty(num, 2), unit: unit || '' };
}

/**
 * Cek apakah qty yang dimasukkan cukup, meski beda satuan
 * Contoh: stok 3kg, kebutuhan 500g → TRUE (cukup)
 * Return: { sufficient: boolean, stockInInputUnit: number, note: string | null }
 */
export function checkSufficiency(stockValue, stockUnit, neededValue, neededUnit) {
  const sUnit = normalizeUnit(stockUnit);
  const nUnit = normalizeUnit(neededUnit);

  if (!stockValue || !neededValue) return { sufficient: false, stockInNeededUnit: stockValue, note: null };

  // Sama satuan — langsung bandingkan
  if (sUnit === nUnit) {
    return {
      sufficient: stockValue >= neededValue,
      stockInNeededUnit: stockValue,
      note: null,
    };
  }

  // Beda satuan tapi satu keluarga → konversi stok ke satuan kebutuhan
  if (isSameFamily(sUnit, nUnit)) {
    const { value: stockConverted, converted } = convertUnit(stockValue, sUnit, nUnit);
    return {
      sufficient: stockConverted >= neededValue,
      stockInNeededUnit: stockConverted,
      note: converted ? `Stok ${formatQty(stockValue, 2)} ${stockUnit} = ${formatQty(stockConverted, 2)} ${neededUnit}` : null,
    };
  }

  // Tidak bisa dikonversi
  return { sufficient: false, stockInNeededUnit: stockValue, note: null };
}

/**
 * Parse input user yang mungkin pakai koma (0,5 → 0.5)
 * Juga menghandle pemisah ribuan (50.000 → 50000)
 */
export function parseUserInput(str) {
  if (!str && str !== 0) return 0;
  let s = String(str).trim();
  s = s.replace(/\s+/g, '');
  const commaCount = (s.match(/,/g) || []).length;
  const dotCount = (s.match(/\./g) || []).length;
  
  if (commaCount > 0 && dotCount > 0) {
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (commaCount > 0) {
    if (/^(\d{1,3})(,\d{3})+$/.test(s)) s = s.replace(/,/g, '');
    else s = s.replace(/,/g, '.');
  } else if (dotCount > 0) {
    if (/^(\d{1,3})(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  }
  return parseFloat(s) || 0;
}

// ─── Konversi Liter ↔ Gram (densitas alkohol/aqua des ~0.8) ─────────────────
// Bahan cair bersatuan "liter" disimpan & diinput dalam GRAM di sistem
// Konversi: gram * 0.8 = ml yang sebenarnya dibutuhkan saat meracik
// Kebalikan: ml / 0.8 = gram yang perlu ditimbang/diambil dari stok

export const LITER_DENSITY = 0.8; // densitas alkohol parfum ~0.8 g/ml

// Kategori bahan yang umumnya bersatuan liter (bukan bibit)
const LITER_KATEGORI = ['alkohol', 'aqua des'];

/**
 * Cek apakah bahan ini perlu konversi liter→gram
 * Bisa digunakan dengan satuan (dari BahanCair entity) atau kategori nama saja
 * - Jika ada satuan: cek satuan === 'liter' && bukan bibit
 * - Jika hanya kategori (SampleForm): cek berdasarkan nama kategori
 */
export function needsLiterConversion(satuan, kategori) {
  const s = (satuan || '').toLowerCase().trim();
  const k = (kategori || '').toLowerCase().trim();
  if (k === 'bibit') return false;
  // Jika ada info satuan liter/L → konversi
  if (s === 'liter' || s === 'l') return true;
  // Jika tidak ada satuan tapi kategorinya termasuk bahan cair liter
  if (!satuan || s === '') {
    return LITER_KATEGORI.some(cat => k === cat || k.startsWith(cat));
  }
  return false;
}

/**
 * Hitung gram dari volume ml yang dibutuhkan
 * ml * 0.8 = gram
 */
export function mlToGram(ml) {
  if (!ml || ml <= 0) return 0;
  return ml * LITER_DENSITY;
}

/**
 * Hitung ml dari gram yang ditimbang
 * gram / 0.8 = ml
 */
export function gramToMl(gram) {
  if (!gram || gram <= 0) return 0;
  return gram / LITER_DENSITY;
}

const fmtLocal = (n, dec = 2) =>
  new Intl.NumberFormat('id-ID', { maximumFractionDigits: dec }).format(n);

/**
 * Format konversi untuk display dengan titik ribuan (id-ID locale)
 * Contoh: 26250g / 21000ml → "26.250g / 21.000ml", atau kalau >=1000g → "26,25kg / 21L"
 */
export function formatGramMl(ml, threshold = 100) {
  const numericMl = Number(ml);
  if (!numericMl || numericMl <= 0) return null;
  const gram = mlToGram(numericMl);

  // gramLabel: < threshold → tampilkan gram, >= threshold → tampilkan kg
  let gramLabel;
  if (gram >= threshold) {
    const kg = gram / 1000;
    gramLabel = `${fmtLocal(kg, 2)}kg`;
  } else {
    gramLabel = `${fmtLocal(gram, 2)}g`;
  }

  // mlLabel: < threshold → ml, >= threshold → L
  let mlLabel;
  if (ml >= threshold) {
    const L = ml / 1000;
    mlLabel = `${fmtLocal(L, 2)}L`;
  } else {
    mlLabel = `${fmtLocal(ml, 2)}ml`;
  }

  return {
    gram: parseFloat(gram.toFixed(2)),
    ml: parseFloat(numericMl.toFixed(2)),
    label: `${mlLabel} / ${gramLabel}`,
    gramLabel,
    mlLabel,
  };
}

/**
 * Dapatkan perkiraan densitas (g/ml) berdasarkan kategori atau nama bahan
 * - Alkohol / Solvent / Absolute / Ethanol / Superfine: 0.80 g/ml (dikali 0.8 ke satuan gram)
 * - Air / Aqua Des: 0.80 g/ml (sesuai standar formulasi Satala dikali 0.8 ke satuan gram)
 * - Selain Alkohol dan Air (Bibit Parfum, DPG, PEG, Oil, dll): 1 : 1 (1.00 g/ml)
 */
export function getIngredientDensity(kategori = '', namaBahan = '') {
  const k = (kategori || '').toLowerCase().trim();
  const n = (namaBahan || '').toLowerCase().trim();
  // Air dan Alkohol dikali 0.8 ke satuan gram
  if (k.includes('alkohol') || n.includes('alkohol') || n.includes('ethanol') || n.includes('absolute') || n.includes('superfine')) return 0.8;
  if (k.includes('aqua') || n.includes('aqua') || n.includes('water') || n.includes('air')) return 0.8;
  // Selain alkohol dan air, 1 : 1 antara gram dan ml (1 ml = 1 gram / 1 L = 1 kg)
  return 1.0;
}

/**
 * Hitung total volume dan berat berdasarkan racikan/formula yang sebenarnya.
 * - Air dan Alkohol dikali 0.8 untuk ke satuan gram/kg
 * - Bibit, DPG, dan bahan lainnya 1 : 1 (dikali 1.0)
 * Jika racikan ada: bobot dihitung per-bahan secara proporsional.
 * Jika racikan belum ada: default bobot cair adalah densitas formula standar (~0.85 kg/L)
 * sehingga tidak pernah salah kaprah 1:1.
 */
export function calculateRacikanDual(volumeMl, racikan = []) {
  const numMl = Number(volumeMl);
  if (!numMl || isNaN(numMl) || numMl <= 0) {
    return {
      volumeMl: 0,
      weightGram: 0,
      density: 0.85,
      volumeText: '—',
      weightText: '—',
      label: '—',
      text: '—',
    };
  }

  let totalWeightGram = 0;
  let hasValidRacikan = false;

  if (Array.isArray(racikan) && racikan.length > 0) {
    let totalPersen = 0;
    racikan.forEach(r => {
      const pFloat = parseFloat(String(r.persentase || 0).replace(',', '.')) || 0;
      if (pFloat > 0) {
        hasValidRacikan = true;
        totalPersen += pFloat;
        const bahanMl = numMl * pFloat / 100;
        const density = getIngredientDensity(r.kategori_bahan || r.kategori, r.nama_bahan || r.nama);
        totalWeightGram += (bahanMl * density);
      } else if (r.kebutuhan_nilai) {
        hasValidRacikan = true;
        const sat = (r.kebutuhan_satuan || r.satuan || '').toLowerCase();
        const density = getIngredientDensity(r.kategori_bahan || r.kategori, r.nama_bahan || r.nama);
        let bMl = 0;
        if (sat === 'l' || sat === 'liter') bMl = Number(r.kebutuhan_nilai) * 1000;
        else if (sat === 'ml') bMl = Number(r.kebutuhan_nilai);
        else if (sat === 'kg') bMl = (Number(r.kebutuhan_nilai) * 1000) / density;
        else if (sat === 'g' || sat === 'gram') bMl = Number(r.kebutuhan_nilai) / density;
        totalWeightGram += (bMl * density);
      }
    });

    if (hasValidRacikan && totalPersen > 0 && totalPersen < 100) {
      const remainingMl = numMl * Math.max(0, 100 - totalPersen) / 100;
      totalWeightGram += (remainingMl * 0.85);
    }
  }

  if (!hasValidRacikan || totalWeightGram <= 0) {
    // Estimasi standar bulk parfum (campuran alkohol/air 0.8 dan bibit/DPG 1.0)
    totalWeightGram = numMl * 0.85;
  }

  const effectiveDensity = totalWeightGram / numMl;
  return formatDualVolumeWeight(numMl, '', '', effectiveDensity);
}

/**
 * Format ganda Volume & Berat (ml, L / g, kg)
 * Contoh:
 * 10.000 ml → "10 L / 8,5 kg"
 * 250 ml → "250 ml / 212,5 g"
 */
export function formatDualVolumeWeight(volumeMl, kategori = '', namaBahan = '', customDensity = null) {
  const numMl = Number(volumeMl);
  if (!numMl || isNaN(numMl) || numMl <= 0) {
    return {
      volumeMl: 0,
      weightGram: 0,
      density: 0.85,
      volumeText: '—',
      weightText: '—',
      label: '—',
      text: '—',
    };
  }

  let density;
  if (customDensity != null) {
    density = customDensity;
  } else if (kategori || namaBahan) {
    density = getIngredientDensity(kategori, namaBahan);
  } else {
    // Jika tanpa bahan spesifik (bulk task/PO), gunakan rata-rata formula parfum ~0.85 kg/L agar tidak 1:1
    density = 0.85;
  }

  const weightGram = numMl * density;

  // Format Volume (L / ml)
  let volumeText;
  if (numMl >= 1000) {
    const L = numMl / 1000;
    volumeText = `${fmtLocal(L, 2)} L`;
  } else {
    volumeText = `${fmtLocal(numMl, 2)} ml`;
  }

  // Format Berat (kg / g)
  let weightText;
  if (weightGram >= 1000) {
    const kg = weightGram / 1000;
    weightText = `${fmtLocal(kg, 2)} kg`;
  } else {
    weightText = `${fmtLocal(weightGram, 2)} g`;
  }

  return {
    volumeMl: numMl,
    weightGram,
    density,
    volumeText,
    weightText,
    label: `${volumeText} / ${weightText}`,
    text: `${volumeText} / ${weightText}`,
  };
}